/* PG-001 Home Hero Carousel Web Component
   ・mgh-home-hero タグでカスタムエレメント定義
   ・自動再生 + 矢印 + ドット + スワイプ + キーボード操作
   ・IntersectionObserver で画面外なら autoplay を停止
   ・prefers-reduced-motion で transition を抑制（CSS 側） */

class MghHomeHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.mgh-home-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.slidesContainer = this.querySelector('[data-slides]');
    this.currentIndex = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.intervalMs = (parseInt(this.dataset.autoplayInterval, 10) || 5) * 1000;
    this.timer = null;
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchSwipeThreshold = 40;

    if (this.slides.length <= 1) {
      // single slide: no carousel behavior needed
      return;
    }

    this.boundOnDotClick = this.onDotClick.bind(this);
    this.boundOnTouchStart = this.onTouchStart.bind(this);
    this.boundOnTouchEnd = this.onTouchEnd.bind(this);
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnFocusIn = this.pauseAutoplay.bind(this);
    this.boundOnFocusOut = this.maybeStartAutoplay.bind(this);
    this.boundOnPointerEnter = this.pauseAutoplay.bind(this);
    this.boundOnPointerLeave = this.maybeStartAutoplay.bind(this);

    this.dots.forEach((dot) => dot.addEventListener('click', this.boundOnDotClick));

    if (this.slidesContainer) {
      this.slidesContainer.addEventListener('touchstart', this.boundOnTouchStart, { passive: true });
      this.slidesContainer.addEventListener('touchend', this.boundOnTouchEnd, { passive: true });
    }

    this.addEventListener('keydown', this.boundOnKeydown);
    this.addEventListener('focusin', this.boundOnFocusIn);
    this.addEventListener('focusout', this.boundOnFocusOut);
    this.addEventListener('mouseenter', this.boundOnPointerEnter);
    this.addEventListener('mouseleave', this.boundOnPointerLeave);

    if (this.autoplay) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.maybeStartAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      }, { threshold: 0.25 });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    if (this.observer) this.observer.disconnect();
    this.dots.forEach((dot) => dot.removeEventListener('click', this.boundOnDotClick));
    if (this.slidesContainer) {
      this.slidesContainer.removeEventListener('touchstart', this.boundOnTouchStart);
      this.slidesContainer.removeEventListener('touchend', this.boundOnTouchEnd);
    }
    this.removeEventListener('keydown', this.boundOnKeydown);
    this.removeEventListener('focusin', this.boundOnFocusIn);
    this.removeEventListener('focusout', this.boundOnFocusOut);
    this.removeEventListener('mouseenter', this.boundOnPointerEnter);
    this.removeEventListener('mouseleave', this.boundOnPointerLeave);
  }

  go(index) {
    const total = this.slides.length;
    const next = ((index % total) + total) % total;
    if (next === this.currentIndex) return;
    this.slides[this.currentIndex].removeAttribute('data-active');
    this.slides[next].setAttribute('data-active', '');
    if (this.dots[this.currentIndex]) this.dots[this.currentIndex].removeAttribute('data-active');
    if (this.dots[next]) this.dots[next].setAttribute('data-active', '');
    this.currentIndex = next;
    if (this.timer) {
      this.pauseAutoplay();
      this.maybeStartAutoplay();
    }
  }

  onDotClick(event) {
    const target = event.currentTarget;
    const index = parseInt(target.dataset.dot, 10);
    if (!isNaN(index)) this.go(index);
  }

  onTouchStart(event) {
    if (!event.touches || event.touches.length === 0) return;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchEnd(event) {
    if (this.touchStartX === null) return;
    const touch = event.changedTouches && event.changedTouches[0];
    if (!touch) {
      this.touchStartX = null;
      this.touchStartY = null;
      return;
    }
    const dx = touch.clientX - this.touchStartX;
    const dy = touch.clientY - this.touchStartY;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > this.touchSwipeThreshold) {
      if (dx < 0) this.go(this.currentIndex + 1);
      else this.go(this.currentIndex - 1);
    }
    this.touchStartX = null;
    this.touchStartY = null;
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.currentIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.currentIndex - 1);
    }
  }

  maybeStartAutoplay() {
    if (!this.autoplay) return;
    if (this.timer) return;
    this.timer = setInterval(() => this.go(this.currentIndex + 1), this.intervalMs);
  }

  pauseAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('mgh-home-hero')) {
  customElements.define('mgh-home-hero', MghHomeHero);
}
